import React, { useState, useEffect } from "react";

import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ScrollPanel } from "primereact/scrollpanel";
import { RadioButton } from "primereact/radiobutton";
import { Calendar } from "primereact/calendar";
import { Message } from "primereact/message"; // Importar el componente Message
import { Panel } from "primereact/panel";
import { Dropdown } from "primereact/dropdown";

// Helper function to add days to a date
const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

// Helper function to format a date as "yyyy-MM-dd"
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper function to format time as "HH:MM"
const formatTime = (date) => {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

// Helper function to parse time strings like "0700" into Date objects
const parseTime = (time, baseDate) => {
  const hours = parseInt(time.slice(0, 2), 10);
  const minutes = parseInt(time.slice(2, 4), 10);
  const date = new Date(baseDate);
  date.setHours(hours, minutes, 0, 0);
  return date;
};

// Updated function to get available dates and times
const getAvailableDatesAndTimes = (schedule, maxDate) => {
  const availableDates = [];
  const availableTimes = {};

  const daysOfWeek = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

  for (let i = 0; i <= 365; i++) {

    const currentDate = addDays(new Date(), i);
    const dayOfWeek = daysOfWeek[currentDate.getDay()];
    const formattedDate = formatDate(currentDate);

    if (schedule[dayOfWeek]) {
      availableDates.push(currentDate);

      // Extract available time slots for the day
      const slots = schedule[dayOfWeek].slots;
      availableTimes[formattedDate] = Object.keys(slots).flatMap((timeRange) => {
        const [start, end] = timeRange.split("-");
        const startTime = parseTime(start, currentDate);
        const endTime = parseTime(end, currentDate);

        // Generate intervals (e.g., 30-minute intervals)
        const interval = schedule[dayOfWeek].interval || 30; // Default to 30 minutes
        const intervals = [];
        let currentStart = startTime;

        while (currentStart < endTime) {
          const nextStart = new Date(currentStart.getTime() + interval * 60000); // Add interval in minutes
          if (nextStart <= endTime) {
            intervals.push({
              start: currentStart,
              end: nextStart,
            });
          }
          currentStart = nextStart;
        }

        return intervals;
      });
    }
  }

  return { availableDates, availableTimes };
};

/**
 * Component to display a list of doctors with radio buttons for selection
 * @param {object} props
 * @param {Array} props.doctors - List of doctors to display
 * @param {number | null} props.selectedDoctor - ID of the currently selected doctor
 * @param {Function} props.onDoctorSelect - Callback when a doctor is selected
 */
const DoctorRadioList = ({
  doctors,        // Doctors
  selectedDoctor, // Selected doctors
  selectedDate,
  selectedTime,
  onDoctorSelect,
  onDateChange,
  onTimeChange,
  showDoctorSelector
}) => {

  if (!doctors || doctors.length === 0) {
    return <p>No hay doctores disponibles</p>;
  }

  // Calculate the date range (15 days from today)
  const today = new Date();
  // const maxDate = addDays(today, 15);

  const doctor = doctors.find((doc) => doc.id === selectedDoctor);

  const { availableDates, availableTimes } = doctor
    ? getAvailableDatesAndTimes(doctor.schedule[0]) //, maxDate)
    : { availableDates: [], availableTimes: {} };

  // Format available dates for the Calendar component
  const disabledDates = Array.from({ length: 365 }, (_, i) => addDays(today, i)).filter(
    (date) => !availableDates.some((availableDate) => formatDate(availableDate) === formatDate(date))
  );

  if (showDoctorSelector || !selectedDoctor) {
    return (
      <ScrollPanel className="doctor-list-scroll">
        <div className="card-container">
          {doctors.map((doctor) => (
            <div
              key={doctor.id}
              className="doctor-card flex flex-row justify-content-between align-items-center cursor-pointer"
              onClick={() => onDoctorSelect(doctor.id)}
            >
              <div className="doctor-card-header flex-grow-1 align-items-center">
                <div className="mr-2 radio-container">
                  <RadioButton
                    inputId={`doctor-${doctor.id}`}
                    name="doctor"
                    value={doctor.id}
                    checked={selectedDoctor === doctor.id}
                    onChange={() => onDoctorSelect(doctor.id)}
                  />
                </div>
                <figure className="m-0 mr-2">
                  <img src={doctor.profileImage} alt={doctor.name} className="round" />
                </figure>
                <div className="flex-grow-1 doctor-name">
                  <span className="font-bold block">{doctor.name}</span>
                  <span className="block">
                    {doctor.specialities.map((s) => s.name).join(", ")}
                  </span>
                  <span className="block">
                    {doctor.phoneNumber}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </ScrollPanel>
    );
  }

  return (
    <div className="doctor-selected-summary">
      <p>
        <strong>Doctor:</strong> {doctor.name} -{" "}
        {doctor.specialities.map((s) => s.name).join(", ")}
      </p>
      <div className="flex align-items-center gap-3">
        <Calendar
          value={selectedDate}
          onChange={(e) => onDateChange(e.value)}
          dateFormat="dd/mm/yy"
          placeholder="Seleccione una fecha"
          showIcon
          minDate={today}
          disabledDates={disabledDates}
        />
        {selectedDate && (
          <Dropdown
            value={selectedTime}
            options={
              availableTimes[formatDate(selectedDate)]?.map((slot) => ({
                label: `${formatTime(slot.start)} - ${formatTime(slot.end)}`,
                value: `${formatTime(slot.start)}-${formatTime(slot.end)}`,
              })) || []
            }
            onChange={(e) => onTimeChange(e.value)}
            placeholder="Seleccione una hora"
          />
        )}
      </div>
    </div>
  );
};

/**
 * Modal to select doctors for consultation requirements
 * @param {object} props
 * @param {boolean} props.visible - Controls the visibility of the modal
 * @param {() => void} props.onHide - Callback to hide the modal
 * @param {Array} props.consultationRequirements - List of consultation requirements
 * @param {object} props.groupedDoctors - Doctors grouped by their specialties
 * @param {boolean} props.loadingDoctors - Loading state for doctors
 * @param {Function} props.onConfirm - Callback to confirm the selected doctors
 * @param {number} props.caseId - ID of the medical case
 * @param {Function} props.bookingsMutation - Mutation function for booking
 * @param {string} props.patientName - Name of the patient
 * @param {string} props.patientPhone - Phone number of the patient
 * @param {string} props.patientDni - DNI of the patient
 * @param {Array} props.bookingsData - Data for existing bookings
 */
export default function DoctorSelectionModal({
  visible,
  onHide,
  consultationRequirements,
  groupedDoctors,
  loadingDoctors,
  onConfirm,
  caseId,
  bookingsMutation,
  patientName,
  patientPhone,
  patientDni,
  bookingsData,
}) {

  const [selectedDoctors, setSelectedDoctors] = useState({});
  const [selectedDates, setSelectedDates] = useState({});
  const [selectedTimes, setSelectedTimes] = useState({});
  const [localGroupedDoctors, setLocalGroupedDoctors] = useState(groupedDoctors);
  const [showDoctorSelectors, setShowDoctorSelectors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");

  // Pre-fill state with bookingsData
  useEffect(() => {

    if (bookingsData?.length > 0) {

      const prefilledDoctors = {};
      const prefilledDates = {};
      const prefilledTimes = {};

      bookingsData.forEach((booking) => {
        if (booking.booking) {

          prefilledDoctors[booking.requirementId] = booking.booking.doctorId;
          const [year, month, day] = booking.booking.date.split("-").map(Number);
          prefilledDates[booking.requirementId] = new Date(year, month - 1, day);
          prefilledTimes[booking.requirementId] = booking.booking.time; 

          // Set the doctor selector to false for the pre-filled doctors
          setShowDoctorSelectors((prev) => ({
            ...prev,
            [booking.requirementId]: false,
          }));
        }
      });

      setSelectedDoctors(prefilledDoctors);
      setSelectedDates(prefilledDates);
      setSelectedTimes(prefilledTimes);
    }
  }, [bookingsData]);

  // Update localGroupedDoctors whenever groupedDoctors changes
  useEffect(() => {
    if (groupedDoctors) {
      setLocalGroupedDoctors(groupedDoctors);
    }
  }, [groupedDoctors]);

  const handleDoctorChange = (requirementId, doctorId) => {

    setSelectedDoctors((prev) => {

      const currentDoctorId = prev[requirementId];

      // If the selected doctor is the same, keep the date and time
      if (currentDoctorId === doctorId) {
        return prev;
      }

      // If a different doctor is selected, reset date and time
      setSelectedDates((prevDates) => ({
        ...prevDates,
        [requirementId]: null,
      }));
      setSelectedTimes((prevTimes) => ({
        ...prevTimes,
        [requirementId]: null,
      }));

      return {
        ...prev,
        [requirementId]: doctorId,
      };
    });

    setShowDoctorSelectors((prev) => ({
      ...prev,
      [requirementId]: false, // Switch to date and time selection for this requirement
    }));
  };

  const handleDateChange = (requirementId, date) => {
    setSelectedDates((prev) => ({
      ...prev,
      [requirementId]: date,
    }));
  };

  const handleTimeChange = (requirementId, time) => {
    setSelectedTimes((prev) => ({
      ...prev,
      [requirementId]: time,
    }));
  };

  const handleModifyAll = () => {
    // Cambiar todas las banderas a true para mostrar el selector de doctores
    const updatedSelectors = {};
    consultationRequirements.forEach((req) => {
      updatedSelectors[req.requirement] = true;
    });
    setShowDoctorSelectors(updatedSelectors);
  };

  const validateSelections = () => {
    for (const req of consultationRequirements) {
      const doctorId = selectedDoctors[req.requirement];
      const date = selectedDates[req.requirement];
      const time = selectedTimes[req.requirement];

      if (doctorId && (!date || !time)) {
        setErrorMessage(
          `Por favor, complete la fecha y la hora para el requisito "${req.requirement_name}".`
        );
        return false;
      }
    }
    setErrorMessage(""); // Limpiar el mensaje de error si todo está bien
    return true;
  };

  const handleConfirm = () => {
    if (validateSelections()) {
      const consultationRequirementsPayload = consultationRequirements
        .filter((req) => {
          const doctorId = selectedDoctors[req.requirement];
          const date = selectedDates[req.requirement];
          const time = selectedTimes[req.requirement];
          return doctorId && date && time; // Only include fully configured requirements
        })
        .map((req) => ({
          requirementId: req.requirement,
          doctorId: selectedDoctors[req.requirement],
          date: formatDate(selectedDates[req.requirement]),
          time: selectedTimes[req.requirement], // Include the time range
          amount: req.amount, // Include the price of the requirement
        }));

      if (consultationRequirementsPayload.length > 0) {
        // Trigger the mutation with additional patient details
        bookingsMutation.mutate(
          {
            medicalCaseId: caseId,
            patientName, // Include patient name
            patientPhone, // Include patient phone
            patientDni, // Include patient DNI
            consultationRequirements: consultationRequirementsPayload,
          },
          {
            onSuccess: () => {
              // Close the modal after a successful response
              onHide();
            },
            onError: (error) => {
              // Optionally handle errors here
              console.error("Error creating booking:", error);
            },
          }
        );
      } else {
        onHide(); // Close the modal if no valid requirements
      }
    }
  };

  const _groupedDoctors = localGroupedDoctors || {};

  // Specialties that are interactive (have doctors available) and are not done yet
  // const interactiveSpecialties = consultationRequirements.filter(
  //   (req) =>
  //     _groupedDoctors[req.requirement] &&
  //     _groupedDoctors[req.requirement].doctors.length > 0 &&
  //     !req.is_done
  // );

  return (
    <Dialog
      header="Este caso clínico tiene consultas médicas por agendar"
      visible={visible}
      onHide={onHide}
      draggable={false}
      className="consultation-modal"
    >
      {loadingDoctors ? (
        <p>Cargando doctores...</p>
      ) : 
        <>
          <h4>Selecciona el doctor para asignar la consulta médica.</h4>
          {consultationRequirements.map((req) => {

            // const booking = bookingsData?.find((booking) => booking.requirementId === req.requirement);

            // Traducción de estados al español
            // const statusTranslations = {
            //   "No asignada": "No asignada",
            //   "Pending": "Pendiente",
            //   "Confirmed": "Confirmada",
            //   "Cancelled": "Cancelada",
            // };

            // const bookingStatus = statusTranslations[booking?.booking?.status] || "No asignada";

            return (
              <Panel
                key={req.id}
                className="mb-3"
                header={
                  <div className="flex justify-content-between align-items-center">
                    <span>{req.requirement_name}</span>
                    {/* <span className={`status-label ${bookingStatus.toLowerCase().replace(" ", "-")}`}>
                      {bookingStatus}
                    </span> */}
                  </div>
                }
              >
                <DoctorRadioList
                  doctors={_groupedDoctors[req.requirement]?.doctors || []}
                  selectedDoctor={selectedDoctors[req.requirement] || null}
                  selectedDate={selectedDates[req.requirement] || null}
                  selectedTime={selectedTimes[req.requirement] || null}
                  onDoctorSelect={(doctorId) => handleDoctorChange(req.requirement, doctorId)}
                  onDateChange={(date) => handleDateChange(req.requirement, date)}
                  onTimeChange={(time) => handleTimeChange(req.requirement, time)}
                  showDoctorSelector={showDoctorSelectors[req.requirement] ?? true}
                />
              </Panel>
            );
          })}
          {errorMessage && (
            <div style={{ marginTop: "1rem" }}>
              <Message severity="error" text={errorMessage} />
            </div>
          )}
        </>
      }
      <div className="delete-confirm-buttons-container">
        <Button label="Modificar" severity="warning" onClick={handleModifyAll} />
        <Button label="Cancelar" severity="danger" onClick={onHide} />
        <Button
          label="Confirmar"
          onClick={handleConfirm}
          disabled={bookingsMutation.isLoading}
        />
      </div>
    </Dialog>
  );
}